import React, { useState } from 'react';
import { TextField } from '@material-ui/core';
import {
  Container,
  Card,
  Header,
  Logo,
  FormHeader,
  Form,
  FormGroup,
  FormLabel,
  CheckboxContainer,
  ButtonContainer,
  SignUpButton,
  Disclaimer,
  PolicyLink,
  Message,
} from './NonMemberSignupForm.styled';
import { formatPhoneNumber } from '../../../../../Component/Analytics/utils';
import { saveNonMemberContact } from '../../../../../redux/services/nonmemberSignUp';
import { getQueryStringValue } from '../../../../../redux/helpers';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const isEmpty = value => !value || value.trim() === '';
const isEmailValid = email => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};
const isValidPhoneNumber = phone => {
  // Use the formatPhoneNumber function to validate
  const formatted = formatPhoneNumber(phone);
  return formatted !== null;
};

const formatPhoneInput = value => {
  // Remove all non-digit characters first
  const digitsOnly = value.replace(/\D/g, '');

  // Limit to 10 digits
  const limitedDigits = digitsOnly.slice(0, 10);

  // Format as user types - apply formatting progressively
  if (limitedDigits.length === 0) return '';
  if (limitedDigits.length <= 3) return `(${limitedDigits}`;
  if (limitedDigits.length <= 6)
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(
    3,
    6
  )}-${limitedDigits.slice(6)}`;
};

const getEmailErrorHelperText = email => {
  if (isEmpty(email)) return 'This field is required';
  if (!isEmailValid(email)) return 'Please enter a valid email address';
  return '';
};

const getPhoneNumberErrorHelperText = phone => {
  if (isEmpty(phone)) return '';
  if (!isValidPhoneNumber(phone))
    return 'Please enter a valid 10-digit phone number';
  return '';
};

const NonMemberSignupForm = ({
  preview = false,
  headerText = '',
  logoUrl = '',
  organizationName = '',
}) => {
  const orgId = getQueryStringValue('orgId');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    website: '', // Honeypot field - hidden from users but bots might fill it
  });

  const [textMessages, setTextMessages] = useState(false);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' }); // type: 'success' or 'error'

  const handleInputChange = (field, value) => {
    if (preview) return;

    // Reset message when user starts typing
    if (message.text) {
      setMessage({ text: '', type: '' });
    }

    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = checked => {
    if (preview) return;

    // Reset message when user changes checkbox
    if (message.text) {
      setMessage({ text: '', type: '' });
    }

    setTextMessages(checked);
  };

  const checkIfRequiredFieldsAreMissing = () => {
    const missingRequiredFields = [];

    if (isEmpty(formData.firstName)) {
      missingRequiredFields.push('First Name');
    }

    if (isEmpty(formData.lastName)) {
      missingRequiredFields.push('Last Name');
    }

    if (isEmpty(formData.email)) {
      missingRequiredFields.push('Email');
    } else if (!isEmailValid(formData.email)) {
      missingRequiredFields.push('Valid Email Address');
    }

    if (
      !isEmpty(formData.phoneNumber) &&
      !isValidPhoneNumber(formData.phoneNumber)
    ) {
      setIsSubmitClicked(false);
      missingRequiredFields.push('Valid Mobile Number');
    }

    // Honeypot validation - if this field is filled, it's likely a bot
    if (!isEmpty(formData.website)) {
      console.log('Honeypot field filled - potential bot submission');
      return true; // Silently reject the submission
    }

    if (missingRequiredFields.length > 0) {
      return true;
    } else {
      return false;
    }
  };

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = async e => {
    if (preview) return;
    e.preventDefault();

    setIsSubmitClicked(true);
    const token = await executeRecaptcha('nonMemberSignUp');
    if (checkIfRequiredFieldsAreMissing()) {
      setIsSubmitClicked(false);
      return;
    }

    console.log('Form submitted:', { ...formData, textMessages });

    // Format phone number for database (remove all non-digit characters)
    const phoneForDatabase = formData.phoneNumber
      ? formData.phoneNumber.replace(/\D/g, '')
      : '';

    let data = {
      ...formData,
      phoneNumber: phoneForDatabase, // Save unformatted phone number to database
      textingOptIn: textMessages,
      organizationId: orgId,
      token: token,
    };
    let savedRes = await saveNonMemberContact(data);
    if (savedRes && savedRes.error) {
      // Show error message
      setMessage({
        text: savedRes.message || 'Error saving contact information',
        type: 'error',
      });
    } else {
      // Show success message
      setMessage({
        text: "You've successfully subscribed. 🥳",
        type: 'success',
      });
      // Clear form data on successful submission
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        website: '', // Clear honeypot field
      });
      setTextMessages(false);
    }

    setIsSubmitClicked(false);
  };

  const privacyPolicyLink =
    'https://firebasestorage.googleapis.com/v0/b/noterouter-live.appspot.com/o/sample%2FNoteRouter%20Privacy%20Policy%202024.pdf?alt=media&token=7303a148-c26e-4e93-8b5c-02e9a4fbee7b';

  return (
    <Container preview={preview}>
      <Card preview={preview}>
        {logoUrl && (
          <Header preview={preview}>
            <Logo preview={preview}>
              <img src={logoUrl} alt="Logo" preview={preview} />
            </Logo>
          </Header>
        )}

        <FormHeader preview={preview}>
          {headerText && headerText.trim() !== '' ? (
            <p dangerouslySetInnerHTML={{ __html: headerText }} />
          ) : (
            `Use this page to tell us what you need from our organization and how
          you want it sent to you. For more valuable and privileged industry
          information, including for our local market, contact us about becoming
          a full member today!`
          )}
        </FormHeader>

        <Form onSubmit={handleSubmit}>
          <FormGroup preview={preview}>
            <FormLabel htmlFor="firstName" preview={preview}>
              First Name*
            </FormLabel>
            <TextField
              id="firstName"
              variant="outlined"
              size="small"
              value={formData.firstName}
              error={isSubmitClicked && isEmpty(formData.firstName)}
              helperText={
                isSubmitClicked && isEmpty(formData.firstName)
                  ? 'This field is required'
                  : ''
              }
              onChange={e => handleInputChange('firstName', e.target.value)}
              disabled={preview}
              fullWidth
              placeholder="Enter first name"
              inputProps={{ maxLength: 50 }}
            />
          </FormGroup>

          <FormGroup preview={preview}>
            <FormLabel htmlFor="lastName" preview={preview}>
              Last Name*
            </FormLabel>
            <TextField
              id="lastName"
              variant="outlined"
              size="small"
              value={formData.lastName}
              error={isSubmitClicked && isEmpty(formData.lastName)}
              helperText={
                isSubmitClicked && isEmpty(formData.lastName)
                  ? 'This field is required'
                  : ''
              }
              onChange={e => handleInputChange('lastName', e.target.value)}
              disabled={preview}
              fullWidth
              placeholder="Enter last name"
              inputProps={{ maxLength: 50 }}
            />
          </FormGroup>

          <FormGroup preview={preview}>
            <FormLabel htmlFor="email" preview={preview}>
              Email*
            </FormLabel>
            <TextField
              id="email"
              variant="outlined"
              size="small"
              value={formData.email}
              error={
                isSubmitClicked &&
                (isEmpty(formData.email) || !isEmailValid(formData.email))
              }
              helperText={
                isSubmitClicked && getEmailErrorHelperText(formData.email)
              }
              onChange={e => handleInputChange('email', e.target.value)}
              disabled={preview}
              fullWidth
              placeholder="Enter email address"
              inputProps={{ maxLength: 100 }}
            />
          </FormGroup>

          <FormGroup preview={preview}>
            <FormLabel htmlFor="phoneNumber" preview={preview}>
              Mobile Number (Optional)
            </FormLabel>
            <TextField
              id="phoneNumber"
              variant="outlined"
              size="small"
              value={formData.phoneNumber}
              error={
                isSubmitClicked &&
                !isEmpty(formData.phoneNumber) &&
                !isValidPhoneNumber(formData.phoneNumber)
              }
              helperText={
                isSubmitClicked &&
                getPhoneNumberErrorHelperText(formData.phoneNumber)
              }
              onChange={e =>
                handleInputChange(
                  'phoneNumber',
                  formatPhoneInput(e.target.value)
                )
              }
              disabled={preview}
              fullWidth
              placeholder="(123) 456-7890"
            />
          </FormGroup>

          {/* Honeypot field - hidden from users but bots might fill it */}
          <div
            style={{
              position: 'absolute',
              left: '-9999px',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
              opacity: 0,
              pointerEvents: 'none',
            }}
          >
            <TextField
              id="website"
              name="website"
              type="text"
              value={formData.website}
              onChange={e => handleInputChange('website', e.target.value)}
              autoComplete="off"
              tabIndex="-1"
              style={{ display: 'none' }}
            />
          </div>

          <CheckboxContainer preview={preview}>
            <input
              type="checkbox"
              id="textMessages"
              checked={textMessages}
              onChange={e => handleCheckboxChange(e.target.checked)}
              disabled={preview}
            />
            <FormLabel htmlFor="textMessages" preview={preview}>
              Check this box if you&apos;d like to receive occasional text
              messages with updates and offers.
            </FormLabel>
          </CheckboxContainer>

          <ButtonContainer preview={preview}>
            <SignUpButton
              type="submit"
              preview={preview}
              disabled={preview || isSubmitClicked}
            >
              {isSubmitClicked ? 'Submitting...' : 'Sign Up'}
            </SignUpButton>
            {message.text && (
              <Message type={message.type}>{message.text}</Message>
            )}
          </ButtonContainer>
        </Form>

        <Disclaimer preview={preview}>
          By submitting your preferences, you are consenting to receive
          recurring messages via email and/or SMS text from{' '}
          <b>{organizationName}</b> via automated NoteRouter technology to the
          email addresses and/or mobile numbers provided. Messages will be sent
          to you periodically and may vary in volume and frequency. Message and
          data rates may apply. You can also reply &quot;STOP&quot; to block
          texts. Review the NoteRouter Terms and Conditions and Privacy Policy{' '}
          <PolicyLink
            $disabled={preview}
            href={preview ? undefined : privacyPolicyLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            here
          </PolicyLink>{' '}
          at any time.
        </Disclaimer>
      </Card>
    </Container>
  );
};

export default NonMemberSignupForm;
